import React, { useState, useEffect } from "react";
import { API_BASE, getAuthHeaders } from "../../api/client";
import { Icon } from "../../ui";

export function NotificationBellIndicator({ enabled, lang }) {
  const on = enabled !== false;
  return (
    <span
      title={on
        ? (lang === "gr" ? "Ειδοποιήσεις email ενεργές" : "Email notifications on")
        : (lang === "gr" ? "Ειδοποιήσεις email απενεργοποιημένες" : "Email notifications off")}
      style={{ display: "inline-flex", color: on ? "var(--green)" : "var(--text-muted)", opacity: on ? 1 : 0.55 }}
      aria-hidden
    >
      <Icon name={on ? "bellFilled" : "bell"} size={15} />
    </span>
  );
}

export function EmailNotificationsToggle({ lang, user }) {
  const canManage = user?.role === "admin" || user?.role === "brand_manager" || user?.role === "hr";
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!canManage) return undefined;
    let cancelled = false;
    setLoading(true);
    fetch(`${API_BASE}/users/me`, { headers: getAuthHeaders() })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data) => {
        if (!cancelled) setEnabled(data.receive_notifications !== false);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [canManage, user?.id]);

  async function handleToggle() {
    const next = !enabled;
    setSaving(true);
    setSaved(false);
    try {
      const r = await fetch(`${API_BASE}/users/me`, {
        method: "PATCH",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ receive_notifications: next }),
      });
      if (!r.ok) throw new Error("patch_failed");
      setEnabled(next);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2000);
    } catch {
      // keep prior value
    } finally {
      setSaving(false);
    }
  }

  if (!canManage) return null;

  return (
    <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--border)" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: "var(--text-primary)" }}>
            {lang === "gr" ? "Ειδοποιήσεις email" : "Email notifications"}
          </div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4, lineHeight: 1.4 }}>
            {lang === "gr"
              ? "Λήψη email για αιτήματα άδειας και ειδοποιήσεις προσωπικού"
              : "Receive email alerts for leave requests and staff notifications"}
          </div>
          {saved && (
            <div style={{ fontSize: 11, fontWeight: 700, color: "#16A34A", marginTop: 6 }}>
              {lang === "gr" ? "Αποθηκεύτηκε" : "Saved"}
            </div>
          )}
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          disabled={loading || saving}
          onClick={handleToggle}
          style={{
            flexShrink: 0,
            width: 44,
            height: 26,
            borderRadius: 999,
            border: "none",
            cursor: loading || saving ? "wait" : "pointer",
            background: enabled ? "var(--green)" : "var(--surface2)",
            position: "relative",
            transition: "background 0.15s ease",
            opacity: loading ? 0.6 : 1,
          }}
        >
          <span
            style={{
              position: "absolute",
              top: 3,
              left: enabled ? 21 : 3,
              width: 20,
              height: 20,
              borderRadius: "50%",
              background: "#fff",
              boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
              transition: "left 0.15s ease",
            }}
          />
        </button>
      </div>
    </div>
  );
}

export function ChangePasswordSection({ lang }) {
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [pwError, setPwError] = useState("");
  const [pwSuccess, setPwSuccess] = useState("");
  const [pwLoading, setPwLoading] = useState(false);

  const fieldsFilled = Boolean(pwCurrent && pwNew && pwConfirm);

  async function handleSubmit(e) {
    e.preventDefault();
    setPwError("");
    setPwSuccess("");

    if (pwNew !== pwConfirm) {
      setPwError(lang === "gr" ? "Οι κωδικοί δεν ταιριάζουν" : "Passwords do not match");
      return;
    }
    if (pwNew.length < 8) {
      setPwError(lang === "gr" ? "Τουλάχιστον 8 χαρακτήρες" : "At least 8 characters");
      return;
    }

    setPwLoading(true);
    try {
      const r = await fetch(`${API_BASE}/users/me`, {
        method: "PATCH",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ current_password: pwCurrent, new_password: pwNew }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        const detail = data.detail;
        setPwError(
          typeof detail === "string"
            ? detail
            : (lang === "gr" ? "Σφάλμα αλλαγής κωδικού" : "Could not change password"),
        );
        return;
      }
      setPwCurrent("");
      setPwNew("");
      setPwConfirm("");
      setPwSuccess(lang === "gr" ? "Ο κωδικός άλλαξε επιτυχώς" : "Password changed successfully");
    } catch {
      setPwError(lang === "gr" ? "Σφάλμα δικτύου" : "Network error");
    } finally {
      setPwLoading(false);
    }
  }

  return (
    <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--border)" }}>
      <div style={{ fontWeight: 700, fontSize: 13, color: "var(--text-primary)" }}>
        {lang === "gr" ? "Αλλαγή Κωδικού" : "Change Password"}
      </div>
      <form onSubmit={handleSubmit} style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
        <input
          type="password"
          className="form-input"
          value={pwCurrent}
          onChange={(e) => setPwCurrent(e.target.value)}
          placeholder={lang === "gr" ? "Τρέχων κωδικός" : "Current password"}
          autoComplete="current-password"
        />
        <input
          type="password"
          className="form-input"
          value={pwNew}
          onChange={(e) => setPwNew(e.target.value)}
          placeholder={lang === "gr" ? "Νέος κωδικός" : "New password"}
          autoComplete="new-password"
        />
        <input
          type="password"
          className="form-input"
          value={pwConfirm}
          onChange={(e) => setPwConfirm(e.target.value)}
          placeholder={lang === "gr" ? "Επιβεβαίωση νέου κωδικού" : "Confirm new password"}
          autoComplete="new-password"
        />
        {pwError && (
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--danger)" }}>
            {pwError}
          </div>
        )}
        {pwSuccess && (
          <div style={{ fontSize: 11, fontWeight: 700, color: "#16A34A" }}>
            {pwSuccess}
          </div>
        )}
        <button
          type="submit"
          className="btn-base btn-green btn-small"
          disabled={!fieldsFilled || pwLoading}
          style={{ alignSelf: "flex-start", marginTop: 2 }}
        >
          {lang === "gr" ? "Αλλαγή" : "Change"}
        </button>
      </form>
    </div>
  );
}
